"use client";

// Клиент API для десктопа. Транспорт тот же, что у мобильного приложения:
// заведение из сабдомена, Bearer-токен, конверт {ok,data}/{ok,error}.
//
// Токен переиспользуется из общего хранилища с мобильным приложением намеренно:
// это один и тот же человек и один и тот же вход. Менеджер, залогинившийся на
// телефоне и открывший десктоп в том же браузере, не должен вводить пароль
// второй раз.
export {
  api,
  getToken,
  setToken,
  clearAuth,
  rememberAuth,
  getName,
  getVenue,
  setVenue,
  networkRestaurant,
  restaurantPrefix,
  useRestaurantPrefix,
  signOut,
  takeRevokedNotice,
  tryRefresh,
} from "@/lib/mobile/api";
export type { ApiResult, ActionName } from "@/lib/mobile/api";

// ── Локации сети ──
//
// `mine` — есть ли у вызывающего доступ. Владелец видит все точки заведения, но
// войти может только туда, где у него membership; без пометки только что
// созданная точка выглядела бы сломанной.
export type Venue = {
  id: string;
  name: string;
  address: string | null;
  timezone: string | null;
  /**
   * Часы работы точки. От них зависит УЧЁТНЫЙ ДЕНЬ: у заведения, которое
   * закрывается после полуночи, ночь относится к прошедшему дню.
   */
  work_hours?: { open: string; close: string; overnight: boolean };
  mine: boolean;
  employees: number;
};

export type VenueList = {
  venues: Venue[];
  currentVenueId: string | null;
  canManage: boolean;
};

// ── Ставки: версии с датой применения ──
export type RateTable = Record<string, Record<string, Record<string, number>>>;
export type MoneyByRole = Record<string, number>;

export type RateVersionRow = {
  from: string;
  note: string | null;
  createdAt: string;
  shift: RateTable;
  hourly: RateTable;
  monthly: MoneyByRole;
  // planned — назначена вперёд и ещё не действует; active — применяется сейчас;
  // past — уже история. Три состояния вместо булева: менеджеру важно различать
  // «применяется» и «было».
  state: "planned" | "active" | "past";
};

export type RateVersionsData = {
  today: string;
  activeFrom: string | null;
  versions: RateVersionRow[];
  current: { shift: RateTable; hourly: RateTable; monthly: MoneyByRole };
  examFine: MoneyByRole;
  // Ночные смены в графике за 90 дней и впереди (07.10): иначе колонки «Ночь» нет.
  hasNightShifts?: boolean;
  // Старая ставка по зоне — платится, когда у должности своей нет (07.10).
  zoneRates?: Record<string, number>;
};

// ── Аттестация ──
export type TestRow = {
  id: string;
  title: string;
  description: string | null;
  scope: "network" | "venue";
  passScore: number;
  timeLimitMin: number | null;
  attemptsAllowed: number | null;
  roleKeys: string[];
  // Срок сдачи 'ГГГГ-ММ-ДД' либо null. Единственное, что включает штраф.
  dueDate: string | null;
  isPublished: boolean;
  questions: number;
  attempts: number;
  passedCount: number;
};

export type TestQuestion = {
  id: string;
  n: number;
  text: string;
  // Вид вопроса (13.08). У `open` вариантов нет: ответ словами, балл ставит
  // управляющий руками.
  kind: "choice" | "open";
  // Картинка к вопросу, data-URL.
  photo: string | null;
  options: string[];
  // Правильные ответы приходят ТОЛЬКО составителю (getTest). У проходящего
  // теста этого поля нет — см. getTestForTaking на сервере.
  correct: number[];
  points: number;
};

export type TestFull = {
  id: string;
  title: string;
  description: string | null;
  material: string | null;
  scope: "network" | "venue";
  passScore: number;
  timeLimitMin: number | null;
  attemptsAllowed: number | null;
  roleKeys: string[];
  dueDate: string | null;
  isPublished: boolean;
  questions: TestQuestion[];
};

export type TestResultRow = {
  id: string;
  userId: string | null;
  name: string;
  avatar: string | null;
  testId: string | null;
  testTitle: string;
  score: number;
  total: number;
  percent: number;
  passScore: number;
  passed: boolean;
  attempt: number;
  verdict: string | null;
  takenAt: string;
};

// ── Типы ответов, которые нужны десктопу ──
//
// Описаны здесь, а не переиспользованы из мобильных экранов: у мобильного
// приложения они живут внутри компонентов, и тащить их оттуда значило бы
// связать два интерфейса на структурах, которые каждый читает по-своему.

export type Claim = {
  userId: string;
  name: string;
  avatar: string | null;
  // Оплата этого человека на этой смене. roleKey зафиксирован при назначении —
  // ставка ищется по нему, а не по текущей роли. hours/amount — правки
  // менеджера (null = считается по ставке роли), pay — что выйдет по факту.
  roleKey: string | null;
  hours: number | null;
  amount: number | null;
  // Опоздание в минутах. null = НЕ отмечали, а не «пришёл вовремя».
  lateMin?: number | null;
  pay: number;
  // Чем решена сумма (07.10, claimPayExplained): график подписывает её словами.
  paySource?: "manual" | "iiko" | "covered" | "shift" | "personal" | "role" | "none";
};

export type Shift = {
  id: string;
  date: string;
  kind: string;
  zone: string;
  capacity: number;
  rate: number;
  // Особая ставка этой смены (банкет); null — по ставкам должностей.
  ownRate?: number | null;
  start_t: string | null;
  end_t: string | null;
  claims: Claim[];
  bookedCount: number;
  mine: boolean;
  selfBookable: boolean;
  canUnbook: boolean;
  openSignup: boolean;
};

export type ScheduleEvent = {
  id: string;
  date: string;
  title: string;
  kind: string | null;
  targetZone: string | null;
};

export type ScheduleData = {
  yearMonth: string;
  bookingOpen: Record<string, boolean>;
  rates: Record<string, unknown>;
  events: ScheduleEvent[];
  shifts: Shift[];
  // Откуда заведение берёт опоздания: off / manual / iiko / bot.
  latenessSource?: string;
  // Праздничные даты ЭТОГО месяца, 'YYYY-MM-DD'. Только месяца: весь список
  // заведения на клиент не едет, а отмечается день по одной дате (setHoliday).
  holidays: string[];
};

export type Membership = {
  id: string;
  role: string | null;
  role_id: string | null;
  role_name: string | null;
  role_level: number;
  role_is_system: boolean;
  capabilities: string[];
  zone: string | null;
  venue_id: string;
};

export type Me = {
  user: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    avatar: string | null;
    birthday?: string | null;
    telegram?: string | null;
  };
  memberships: Membership[];
  // Какие модули включил оператор: ключ → включён. Отсутствие ключа трактуем
  // как «включён» — так же, как сервер (см. modules.ts): иначе новый модуль
  // прятал бы раздел у всех, пока оператор до него не дойдёт.
  modules?: Record<string, boolean>;
  /** Точки с подключённой кассой iiko — см. me на сервере. */
  iikoVenues?: string[];
};

export type TourStatus = "done" | "skipped" | "none";

/** «пройдено / пропущено / не открывал» — одна подпись на весь десктоп. */
export const TOUR_STATUS_LABEL: Record<TourStatus, string> = { done: "пройдено", skipped: "пропущено", none: "не открывал" };

export type EmployeeRow = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  avatar: string | null;
  telegram: string | null;
  /**
   * Привязан ли бот НА САМОМ ДЕЛЕ (есть `tg_chat_id`), а не «указан @ник».
   *
   * Разница не косметическая: бот не умеет писать по @username, ему нужен
   * числовой chat_id, а тот появляется, только когда человек сам нажал «Старт».
   * Пока экран показывал @ник, «Telegram есть» и «уведомление дойдёт» выглядели
   * одним и тем же — на этом и сгорела постановка задачи 09.09.
   */
  tg_linked: boolean;
  /** Обучение (23.09): прошёл ли короткое обучение на телефоне и десктопе. */
  tour?: { phone: TourStatus; desk: TourStatus };
  birthday: string | null;
  is_active: boolean;
  memberships: {
    id: string;
    role: string | null;
    role_id: string | null;
    role_name: string | null;
    role_level: number | null;
    zone: string | null;
    venue_id: string;
    // Отпуск (21.09, [116]): 'ГГГГ-ММ-ДД' включительно, пусто — не в отпуске.
    vacation_from?: string | null;
    vacation_until?: string | null;
  }[];
};

/** Сегодня (по часам браузера) внутри отпуска — пометка «в отпуске». */
export function onVacation(m: { vacation_from?: string | null; vacation_until?: string | null } | undefined): boolean {
  if (!m?.vacation_from || !m?.vacation_until) return false;
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return m.vacation_from <= today && today <= m.vacation_until;
}

/** «до 30 сент.» — коротко для пометки. */
export function vacationUntilLabel(until: string): string {
  const [y, mo, d] = until.split("-").map(Number);
  return new Date(y, mo - 1, d).toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

export type TenantRole = {
  id: string;
  key: string;
  name: string;
  level: number;
  is_system: boolean;
  assignable?: boolean;
  editable?: boolean;
  capabilities?: string[];
  // Зоны, где роль работает. Пусто = зона не важна, одна ставка на любую зону
  // (управляющий). Непусто = роль выходит только в эти зоны.
  zones?: string[];
};

// ── Архив инвентаризаций (DESK2) ──
//
// `finished` считается по closed_at, а не по статусу: сессия, закрытая
// автоматически при старте следующей, лежит в базе как closed без даты
// закрытия. Прочерк в «Завершена» — норма, а не потеря данных.
export type ArchiveSession = {
  id: string;
  name: string;
  isActive: boolean;
  finished: boolean;
  startedAt: string;
  closedAt: string | null;
  startedBy: string | null;
  positions: number;
  /** Номер документа в iiko, если инвентаризация уже отправлена (Р3-6). */
  posDocumentNumber: string | null;
  /** Состояние отправки в iiko (28.10, Б20): none · sending · partial · uncertain · done. */
  posPushState?: "none" | "sending" | "partial" | "uncertain" | "done";
};

// Строка свода одной инвентаризации. `mode` — режим замера: у «формулы» есть
// разбивка на открытые (мл) и запечатанные (штуки), у штучных её нет.
export type SummaryRow = {
  item_id: string;
  name: string;
  mode: "formula" | "count";
  unit: string;
  total: number;
  open_ml: number;
  full_count: number;
  note: string;
};

export type CompareRow = {
  item_id: string;
  name: string;
  state: "both" | "only_a" | "only_b" | "unit_mismatch";
  unit: string;
  unit_a: string | null;
  unit_b: string | null;
  total_a: number | null; // null = позиция не замерена, это НЕ ноль
  total_b: number | null;
  delta: number | null;
  delta_pct: number | null;
  note: string;
};

export type CompareData = {
  a: ArchiveSession; // ранняя по дате начала
  b: ArchiveSession; // поздняя
  swapped: boolean; // сервер переставил порядок относительно запроса
  rows: CompareRow[];
  counts: Record<CompareRow["state"], number>;
};

/**
 * Ответ `getOwnerDashboard` — месяц заведения цифрами.
 *
 * Тип лежит здесь, а не в экране, потому что читают его ТРОЕ: сводка
 * (`DashboardSection`), сравнение периодов (`CompareSection`) и подробности
 * показателя (`MetricDetail`). Пока у каждого была своя копия, копии
 * разъезжались: в сравнении у `deviations` было объявлено одно поле из пяти, и
 * тот же объект нельзя было передать в подробности.
 */
export type DashboardData = {
  month: string;
  /** По какой день посчитан месяц (запрос с `until`), null — целиком. */
  upTo?: string | null;
  /** Полгода помесячно — ряд для любого графика динамики. */
  trend: { month: string; fot: number; shifts: number; missed: number; hours: number }[];
  /**
   * С чем сравнивались проценты (22.09). У идущего месяца — по сегодня против
   * тех же дней прошлого («1–22 сентября» / «1–22 августа»), иначе месяц к
   * месяцу. `shiftsPct` — процент «должно было выйти» (смены + невыходы) для
   * последнего столбца графика смен: сам график считал бы его к полному месяцу.
   */
  compare: {
    partial: boolean;
    now: { from: string; to: string };
    prev: { from: string; to: string };
    nowLabel: string;
    prevLabel: string;
    shiftsPct: number | null;
  };
  payroll: { total: number; prevTotal: number; changePct: number | null; perShift: number | null };
  work: {
    shifts: number;
    missed: number;
    people: number;
    hours: number;
    zones: { zone: string; hours: number; shifts: number; share: number }[];
  };
  writeoffs: {
    count: number;
    byReason: { reason: string; count: number }[];
    top: { name: string; unit: string; qty: number; count: number }[];
  };
  deviations: {
    a: string;
    b: string;
    closedAt: string | null;
    summary: {
      compared: number;
      dropped: number;
      grew: number;
      skipped: number;
      top: { name: string; unit: string; delta: number; delta_pct: number | null }[];
    };
  } | null;
  /**
   * Выручка месяца из `daily_revenue` — руками, из файла или из кассы (16.09).
   * `month: null` — ни одного дня с выручкой; это «не измерено», а не ноль.
   */
  revenue: {
    month: number | null;
    days: number;
    lastDay: { date: string; amount: number; source: string } | null;
    avgCheck: number | null;
    changePct: number | null;
    sources: string[];
  };
  /** Чего на экране нет и почему — по данным, а не по списку: ключи `revenue`, `foodcost`. */
  unavailable: { key: "revenue" | "foodcost"; label: string; why: string }[];
};

// Старшая роль среди membership'ов: старшинство задаёт уровень, не список имён.
export function topRole(memberships: Membership[]): Membership | null {
  return (
    [...memberships].sort(
      (a, b) => (a.role_level ?? Number.MAX_SAFE_INTEGER) - (b.role_level ?? Number.MAX_SAFE_INTEGER),
    )[0] ?? null
  );
}

/**
 * Роль на ВЫБРАННОЙ локации.
 *
 * С появлением сети «моя роль» перестала быть одной: на одной точке человек
 * управляющий, на другой выходит барменом. Интерфейс обязан спрашивать роль той
 * точки, с которой сейчас работают, — иначе, переключившись на точку, где он
 * рядовой сотрудник, он увидит разделы владельца и упрётся в отказы сервера на
 * каждой кнопке (сервер-то смотрит на membership выбранной точки).
 *
 * Точка не выбрана или своего membership на ней нет — падаем на старшую роль:
 * так ведёт себя и сервер, выбирая точку по умолчанию.
 */
export function membershipFor(
  memberships: Membership[],
  venueId: string | null,
): Membership | null {
  if (venueId) {
    const own = memberships.find((m) => m.venue_id === venueId);
    if (own) return own;
  }
  return topRole(memberships);
}

// Есть ли право. Системная роль (владелец) — вершина: у неё есть всё, включая
// права, добавленные в код после того, как роль записали в базу.
//
// Массив читается как «любое ИЗ», как и в гейте диспетчера: к одной вкладке
// ведут разные должности — ведомость смотрит и тот, кто ведёт смены, и
// бухгалтер, который их не ведёт. Семантику держим одинаковой с сервером
// специально: разъехавшись, экран начнёт показывать пункт, на который сервер
// отвечает отказом.
export function can(m: Membership | null, capability: string | readonly string[]): boolean {
  if (!m) return false;
  if (m.role_is_system) return true;
  const required = typeof capability === "string" ? [capability] : capability;
  return required.some((cap) => m.capabilities.includes(cap));
}

export const ZONE_LABEL: Record<string, string> = {
  bar: "Бар",
  hall: "Зал",
  kitchen: "Кухня",
  housekeeping: "Хозчасть",
};

export const KIND_LABEL: Record<string, string> = {
  day: "День",
  night: "Ночь",
  custom: "Своя",
};
